import * as D from 'date-fns';
import { parse } from 'date-fns';

export const TimePeriod = {
	YEARS_AGO: 'YEARS_AGO',
	LAST_YEAR: 'LAST_YEAR',
	MONTHS_AGO: 'MONTHS_AGO',
	LAST_MONTH: 'LAST_MONTH',
	WEEKS_AGO: 'WEEKS_AGO',
	LAST_WEEK: 'LAST_WEEK',
	DAYS_AGO: 'DAYS_AGO',
	YESTERDAY: 'YESTERDAY',
	NOW: 'NOW',
	TODAY_UPCOMING: 'TODAY_UPCOMING',
	LATER_TODAY: 'LATER_TODAY',
	TODAY_PAST: 'TODAY_PAST',
	FUTURE: 'FUTURE',
	PAST: 'PAST',
	TOMORROW: 'TOMORROW',
	DAYS_FROM_NOW: 'DAYS_FROM_NOW',
	NEXT_WEEK: 'NEXT_WEEK',
	WEEKS_FROM_NOW: 'WEEKS_FROM_NOW',
	NEXT_MONTH: 'NEXT_MONTH',
	MONTHS_FROM_NOW: 'MONTHS_FROM_NOW',
	NEXT_YEAR: 'NEXT_YEAR',
	YEARS_FROM_NOW: 'YEARS_FROM_NOW',
	INVALID: 'INVALID',
};

/**
 *
 * @param start {Date}
 * @param end {Date}
 * @param today {Date}
 * @return {string} Period
 */
export function getPeriod(start, end, today) {
	const differenceInDays = D.differenceInCalendarDays(start, today);
	switch (true) {
		case differenceInDays <= -730:
			return TimePeriod.YEARS_AGO;
		case differenceInDays <= -365:
			return TimePeriod.LAST_YEAR;
		case differenceInDays <= -60:
			return TimePeriod.MONTHS_AGO;
		case differenceInDays <= -30:
			return TimePeriod.LAST_MONTH;
		case differenceInDays <= -14:
			return TimePeriod.WEEKS_AGO;
		case differenceInDays <= -7:
			return TimePeriod.LAST_WEEK;
		case differenceInDays <= -2:
			return TimePeriod.DAYS_AGO;
		case differenceInDays <= -1:
			return TimePeriod.YESTERDAY;
		case differenceInDays === 0: {
			if (D.isWithinInterval(today, { start, end })) {
				return TimePeriod.NOW;
			}
			// If start is after now, but before 5:00pm
			if (D.isAfter(start, today) && D.getHours(start) < 17) {
				return TimePeriod.TODAY_UPCOMING;
			}
			if (D.isAfter(start, today)) {
				return TimePeriod.LATER_TODAY;
			}
			return TimePeriod.TODAY_PAST;
		}
		case differenceInDays >= 730:
			return TimePeriod.YEARS_FROM_NOW;
		case differenceInDays >= 365:
			return TimePeriod.NEXT_YEAR;
		case differenceInDays >= 60:
			return TimePeriod.MONTHS_FROM_NOW;
		case differenceInDays >= 30:
			return TimePeriod.NEXT_MONTH;
		case differenceInDays >= 14:
			return TimePeriod.WEEKS_FROM_NOW;
		case differenceInDays >= 7:
			return TimePeriod.NEXT_WEEK;
		case differenceInDays >= 2:
			return TimePeriod.DAYS_FROM_NOW;
		case differenceInDays >= 1:
			return TimePeriod.TOMORROW;
		default:
			return TimePeriod.INVALID;
	}
}

export function createTimePeriodDispatcher(callbacks, defaultValue = '') {
	return function _createTimePeriodDispatcher(start, end, today = new Date()) {
		const period = getPeriod(start, end, today);

		const call = fnName => {
			const fnToCall = callbacks[fnName];
			if (fnToCall) return fnToCall(start, end);
			return defaultValue;
		};

		switch (period) {
			case TimePeriod.YEARS_AGO:
				return call(TimePeriod.YEARS_AGO);
			case TimePeriod.LAST_YEAR:
				return call(TimePeriod.LAST_YEAR);
			case TimePeriod.MONTHS_AGO:
				return call(TimePeriod.MONTHS_AGO);
			case TimePeriod.LAST_MONTH:
				return call(TimePeriod.LAST_MONTH);
			case TimePeriod.WEEKS_AGO:
				return call(TimePeriod.WEEKS_AGO);
			case TimePeriod.LAST_WEEK:
				return call(TimePeriod.LAST_WEEK);
			case TimePeriod.DAYS_AGO:
				return call(TimePeriod.DAYS_AGO);
			case TimePeriod.YESTERDAY:
				return call(TimePeriod.YESTERDAY);
			case TimePeriod.NOW:
				return call(TimePeriod.NOW);
			case TimePeriod.TODAY_UPCOMING:
				return call(TimePeriod.TODAY_UPCOMING);
			case TimePeriod.LATER_TODAY:
				return call(TimePeriod.LATER_TODAY);
			case TimePeriod.TODAY_PAST:
				return call(TimePeriod.TODAY_PAST);
			case TimePeriod.FUTURE:
				return call(TimePeriod.FUTURE);
			case TimePeriod.PAST:
				return call(TimePeriod.PAST);
			case TimePeriod.INVALID:
				return call(TimePeriod.INVALID);
			case TimePeriod.TOMORROW:
				return call(TimePeriod.TOMORROW);
			case TimePeriod.DAYS_FROM_NOW:
				return call(TimePeriod.DAYS_FROM_NOW);
			case TimePeriod.NEXT_WEEK:
				return call(TimePeriod.NEXT_WEEK);
			case TimePeriod.WEEKS_FROM_NOW:
				return call(TimePeriod.WEEKS_FROM_NOW);
			case TimePeriod.NEXT_MONTH:
				return call(TimePeriod.NEXT_MONTH);
			case TimePeriod.MONTHS_FROM_NOW:
				return call(TimePeriod.MONTHS_FROM_NOW);
			case TimePeriod.NEXT_YEAR:
				return call(TimePeriod.NEXT_YEAR);
			case TimePeriod.YEARS_FROM_NOW:
				return call(TimePeriod.YEARS_FROM_NOW);
			default:
				return defaultValue;
		}
	};
}

export const isPastMeeting = createTimePeriodDispatcher(
	{
		[TimePeriod.YEARS_AGO]: () => true,
		[TimePeriod.LAST_YEAR]: () => true,
		[TimePeriod.MONTHS_AGO]: () => true,
		[TimePeriod.LAST_MONTH]: () => true,
		[TimePeriod.WEEKS_AGO]: () => true,
		[TimePeriod.LAST_WEEK]: () => true,
		[TimePeriod.DAYS_AGO]: () => true,
		[TimePeriod.YESTERDAY]: () => true,
		[TimePeriod.PAST]: () => true,
		[TimePeriod.TODAY_PAST]: () => true,
	},
	false,
);

export const isHappeningNow = createTimePeriodDispatcher({ [TimePeriod.NOW]: () => true }, false);

export const isMonthsAgo = createTimePeriodDispatcher({ [TimePeriod.MONTHS_AGO]: () => true }, false);

/**
 * Is the current date time between the given start and end dates?
 * @param start {Date}
 * @param end {Date}
 * @return {Boolean}
 */
export function isNowBetween(start, end) {
	const now = new Date();
	return D.isWithinInterval(now, { start, end });
}

export const formatDayOfWeek = date => D.format(date, 'EEEE');
export const formatDayOfMonth = date => D.format(date, 'MMM d');
//January 3, 2020
export const formatLongMonth = date => D.format(date, 'LLLL d, yyyy');

export const formatMonth = date => D.format(date, 'LLLL');

export const ignoreTimestamp = date => {
	const reformat = new Date(date).toISOString().split('T')[0];
	return parse(reformat, 'yyyy-MM-dd', new Date());
};

/**
 * Check if start and end times are at different times of the day (am vs. pm)
 * @param startDateTime {Date}
 * @param endDateTime {Date}
 * @return {string}
 */
export function formatTimeRange(startDateTime, endDateTime) {
	const startTimeOfDay = D.format(startDateTime, 'aaaaa');
	const endTimeOfDay = D.format(endDateTime, 'aaaaa');
	let start = D.format(startDateTime, 'h:mm');
	const end = D.format(endDateTime, "h:mmaaaaa'm'");
	if (startTimeOfDay !== endTimeOfDay) {
		start = D.format(startDateTime, "h:mmaaaaa'm'");
	}

	return `${start} - ${end}`;
}

export function formatDateToText(date) {
	return D.format(new Date(date), 'cccc, MMM d');
}

export function formatDate(date) {
	return D.format(new Date(date), 'M/d/yyyy');
}

export function formatShortDate(date) {
	return D.format(new Date(date), 'M/d');
}

export function formatTime(date) {
	return D.format(new Date(date), "h:mmaaaaa'm'");
}

export function formatDateTime(date) {
	return D.format(new Date(date), 'yyyy/MM/dd HH:mm:ss');
}

export function startOfDayString(origDate) {
	const date = new Date(origDate);
	date.setMilliseconds(0);
	date.setSeconds(0);
	date.setMinutes(0);
	date.setHours(0);
	return date.toISOString();
}
